var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/shared.ts
import { getEnvString } from "@base/environment/core";
import { nanoid } from "@base/utils/crypto";
import { stringify } from "@base/utils/json";
import { A, G } from "@mobily/ts-belt";
import { match } from "ts-pattern";
import { getSafeAsyncContext } from "./async-context-safe.js";
var LOG_LEVELS = {
  trace: { numeric: 5, priority: 0 },
  debug: { numeric: 4, priority: 0 },
  info: { numeric: 3, priority: 1 },
  warn: { numeric: 2, priority: 2 },
  error: { numeric: 1, priority: 3 },
  fatal: { numeric: 0, priority: 3 },
  silent: { numeric: -Infinity, priority: 4 }
};
function getLogLevelNumeric(level) {
  if (G.isNullable(level)) {
    return LOG_LEVELS.info.numeric;
  }
  return match(level.toLowerCase()).with("trace", () => LOG_LEVELS.trace.numeric).
  with("debug", () => LOG_LEVELS.debug.numeric).with("info", () => LOG_LEVELS.info.
  numeric).with("warn", () => LOG_LEVELS.warn.numeric).with("error", () => LOG_LEVELS.
  error.numeric).with("fatal", () => LOG_LEVELS.fatal.numeric).with("silent", () => LOG_LEVELS.
  silent.numeric).otherwise(() => LOG_LEVELS.info.numeric);
}
__name(getLogLevelNumeric, "getLogLevelNumeric");
function shouldLog(messageLevel, currentLevel) {
  const messagePriority = LOG_LEVELS[messageLevel]?.priority ?? 1;
  const currentPriority = LOG_LEVELS[currentLevel]?.priority ?? 1;
  return messagePriority >= currentPriority;
}
__name(shouldLog, "shouldLog");
function resolveServiceName(serviceName, fallback = "pulse-service") {
  return serviceName || getEnvString("NEXT_PUBLIC_APP_NAME") || fallback;
}
__name(resolveServiceName, "resolveServiceName");
function generateSessionId() {
  return nanoid(8);
}
__name(generateSessionId, "generateSessionId");
function mergeWithAsyncContext(providedContext) {
  const asyncContext = getSafeAsyncContext();
  return { ...asyncContext, ...providedContext };
}
__name(mergeWithAsyncContext, "mergeWithAsyncContext");
function formatContext(context, format = "string") {
  if (G.isNullable(context) || A.isEmpty(Object.keys(context))) {
    return "";
  }
  return match(format).with("object", () => ` ${stringify(context)}`).with("stri\
ng", () => ` ${stringify(context)}`).otherwise(() => ` ${stringify(context)}`);
}
__name(formatContext, "formatContext");
function createBaseContext(serviceName, sessionId) {
  return {
    service: serviceName,
    session: sessionId,
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    environment: getEnvString("NEXT_PUBLIC_ENVIRONMENT") || "development"
  };
}
__name(createBaseContext, "createBaseContext");
function formatTimestamp() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
__name(formatTimestamp, "formatTimestamp");
function formatServiceTag(serviceName) {
  return `[${serviceName}]`;
}
__name(formatServiceTag, "formatServiceTag");
function formatSessionTag(sessionId) {
  return `[${sessionId}]`;
}
__name(formatSessionTag, "formatSessionTag");
function formatLevelTag(level) {
  return `[${level.toUpperCase()}]`;
}
__name(formatLevelTag, "formatLevelTag");
function formatLogMessage(level, message, options) {
  const { serviceName, sessionId, context, format = "object" } = options;
  const timestamp = formatTimestamp();
  const service = formatServiceTag(serviceName);
  const session = sessionId ? formatSessionTag(sessionId) : "";
  const levelTag = formatLevelTag(level);
  const contextStr = formatContext(context, format);
  return match(format).with("object", () => {
    const logObject = {
      timestamp,
      level: level.toUpperCase(),
      service: serviceName,
      session: sessionId,
      message,
      ...context
    };
    return stringify(logObject);
  }).with("string", () => `${timestamp} ${service}${session} ${levelTag} ${message}${contextStr}`).
  otherwise(() => `${timestamp} ${service}${session} ${levelTag} ${message}${contextStr}`);
}
__name(formatLogMessage, "formatLogMessage");
function isConsoleAvailable() {
  return typeof console !== "undefined";
}
__name(isConsoleAvailable, "isConsoleAvailable");
function getConsoleMethod(level) {
  return match(level.toLowerCase()).with("error", "fatal", "fail", () => console.
  error).with("warn", () => console.warn).with("info", "success", "ready", "star\
t", () => console.info).with("debug", "trace", () => console.debug).otherwise(() => console.
  log);
}
__name(getConsoleMethod, "getConsoleMethod");
export {
  LOG_LEVELS,
  createBaseContext,
  formatContext,
  formatLevelTag,
  formatLogMessage,
  formatServiceTag,
  formatSessionTag,
  formatTimestamp,
  generateSessionId,
  getConsoleMethod,
  getLogLevelNumeric,
  isConsoleAvailable,
  mergeWithAsyncContext,
  resolveServiceName,
  shouldLog
};
